import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useNavigate } from 'react-router';
import { print } from 'graphql/language/printer';
import withFeatureFlags from '../FeatureFlags/withFeatureFlags';
import { useCurrentUser } from '../../contexts/CurrentUserProvider';
import { useLogger } from '../../contexts/Logger';
import { useUnifiedAnalytics } from '../../contexts/AnalyticsWrapper';
import { usePolling } from '../../utils/hooks';
import Loading from '../Loading/Loading';
import Button from '../Form/Button/Button';
import Modal from '../Modal/Modal';
import HowToFundPopup from '../Dashboard/HowToFundPopup/HowToFundPopup';
import FatcaPopup from '../FatcaPopup/FatcaPopup';
import NewDashboardMaturityBanner from './components/NewDashboardMaturityBanner';
import { Feedback } from '../Feedback/Feedback';
import { submitFeedback } from '../Feedback/FeedbackServiceClient';
import { productKeys, productTypes } from '../../utils/productHelpers';
import { filterMaturingAccountsForBanner } from './components/NewDashboardMaturityBanner/NewDashboardMaturityBanner';

import { GET_CUSTOMER_AND_ACCOUNTS } from './queries/customerAndAccountsQuery';
import { useTooltips, useCarousel, useAccountActions } from './hooks';
import { hasIsaAccount } from './utils/isaHelpers';
import { WelcomeHeader, AccountsList, GlobalTooltips, IsaTransferBanner } from './components';

import './newdashboard.scss';

/** @import { DashboardNewProps, CustomerAndAccountsResponse } from './DashboardNewTypes' */

/**
 * Modal shown when additional account is created from secondary application form
 * @param {{ closePopup: () => void }} props
 */
function AdditionalAccountCreatedModal({ closePopup }) {
    return (
        <Modal className="modal modal__fixed-height" hideClose={true}>
            <h4 className="modal__title">Please bear with us</h4>
            <p className="modal__paragraph">Your new account may take a little while to appear in your dashboard - if
                you don't see it immediately, try refreshing the page in about five minutes.</p>
            <div className="modal__spacer" />
            <Button size='med' label="Got it" onClick={closePopup} />
        </Modal>
    );
}

/**
 * Notification shown when customer has no accounts yet
 * @param {{ message: string }} props
 */
function NoAccountNotification({ message }) {
    return (
        <div className="dashboard-welcome__subtitle">
            {message}
        </div>
    );
}

/**
 * Component shown while account is being created - polls for account data
 * @param {{ customerId: string; onAccountCreated: (data: any) => void }} props
 */
function AccountCreating({ customerId, onAccountCreated }) {
    const [startPolling, setStartPolling] = useState(true);
    const currentUser = useCurrentUser();

    usePolling(async () => {
        if (!startPolling || !currentUser)
            return;

        try {
            const accessToken = currentUser.accessToken;

            const result = await fetch('/graphql', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    query: print(GET_CUSTOMER_AND_ACCOUNTS),
                    variables: { id: customerId }
                }),
            });

            if (result.status === 200) {
                /** @type {{ data: CustomerAndAccountsResponse }} */
                const resultBody = await result.json();
                if (resultBody.data.customer.accounts && resultBody.data.customer.accounts.length !== 0) {
                    setStartPolling(false);
                    if (onAccountCreated) {
                        onAccountCreated(resultBody.data);
                    }
                }
            }
        } catch {
            // Silent catch - polling will continue
        }
    }, [startPolling], 5000);

    return (
        <div className="dashboard-welcome__notification">
            We're busy loading your account
            <div>
                <div className="lds-default">
                    <div></div><div></div><div></div><div></div>
                    <div></div><div></div><div></div><div></div>
                    <div></div><div></div><div></div><div></div>
                </div>
            </div>
        </div>
    );
}

/**
 * NewDashboard - Main dashboard component (refactored)
 * This is the orchestration component that brings together all sub-components.
 * @param {DashboardNewProps} props
 */
function NewDashboard({ featureEnabled, toAccount }) {
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const logger = useLogger();
    const analytics = useUnifiedAnalytics();
    const [modalOpen, setModalOpen] = useState(false);
    const [showFatcaPopup, setShowFatcaPopup] = useState(true);
    const [showAdditionalAccountPopup, setShowAdditionalAccountPopup] = useState(false);
    const [lazyLoadedCustomer, setLazyLoadedCustomer] = useState(/** @type {any | null} */ (null));

    // Custom hooks
    const carouselEnabled = useCarousel();
    const {
        selectedAccountActivity,
        flippedAccountId,
        actionType,
        startWithdrawal,
        startDeposit,
        unflipCard,
        toggleAccountActivity,
        setActionType
    } = useAccountActions();
    
    const {
        openTooltipId,
        popupPosition,
        openMaturityTooltipId,
        maturityPopupPosition,
        openFundingTooltipId,
        fundingPopupPosition,
        openRateChangeTooltipId,
        rateChangePopupPosition,
        showIsaTooltip,
        handleInfoIconClick,
        handleMaturityInfoIconClick,
        handleAvailableBalanceIconClick,
        handleFundingInfoIconClick,
        handleRateChangeInfoIconClick,
        toggleIsaTooltip
    } = useTooltips();

    // GraphQL query
    const { loading, data, error } = useQuery(GET_CUSTOMER_AND_ACCOUNTS, {
        variables: { id: currentUser ? currentUser.customerId : undefined },
        skip: !currentUser || !currentUser.customerId
    });

    // Logging
    useEffect(() => {
        logger.info('New Dashboard Page Loaded');
    }, [logger]);

    // Check if user came from secondary application form
    useEffect(() => {
        const qs = window.location.search.replace(/\?/g, '').split('&');
        const hasComeFromSecondaryAppForm = qs.filter(v => v.match(/^applicationAccepted=/));
        if (hasComeFromSecondaryAppForm && hasComeFromSecondaryAppForm.length === 1) {
            const productKey = qs.filter(v => v.match(/^productKey=/));
            productKey && productKey.length === 1 && sessionStorage.setItem(`app-accepted-${productKey[0].split('=')[1]}`, 'true');
            setShowAdditionalAccountPopup(true);
        }
    }, []);

    // Error logging
    if (error) {
        logger.error('Failed to load customer data for new dashboard', {}, error);
    }

    // Handler for when account is created via polling
    const onAccountCreated = (/** @type {any} */ loadedCustomer) => {
        setLazyLoadedCustomer(loadedCustomer);
    };

    // Close additional account popup and clean URL
    const closeAdditionalAccountPopup = () => {
        setShowAdditionalAccountPopup(false);
        window.history.replaceState({}, document.title, "/");
    };

    // Analytics handlers
    const howToFundClick = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                "how to make a deposit",
                `${window.location.origin}`,
                "dashboard",
                "deposit-product",
                "",
                {},
                "link click",
                "dashboard|click|how to make a deposit",
                'Dashboard'
            );
        }
        setModalOpen(true);
    };

    const openNewAccountClick = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                "open another account",
                `${window.location.origin}`,
                "dashboard",
                "new-account",
                "",
                {},
                "link click",
                "dashboard|click|open another account",
                'Dashboard'
            );
        }
        window.location.href = '/new-account';
    };

    const startIsaTransfer = (/** @type {{ id: string, transferIn?: { status?: string } }} */ account) => {
        navigate(`/isa-transfer/${account.id}`, {
            state: {
                transferInStatus: account.transferIn?.status,
                transferInStatusKnown: true
            }
        });
    };

    // Loading state
    if (loading || !currentUser || !currentUser.customerId || !data) {
        return <Loading message="We're loading your dashboard..." />;
    }

    // Use lazy loaded customer data if available (from polling), otherwise use query data
    /** @type {any} */
    const customerData = lazyLoadedCustomer === null ? data : lazyLoadedCustomer;
    /** @type {any} */
    const customer = customerData.customer;
    /** @type {any[]} */
    const accounts = customer.accounts || [];
    const isFisaMaturityEnabled = featureEnabled && featureEnabled('EnableFisaMaturity');
    const visibleAccounts = isFisaMaturityEnabled
        ? accounts
        : accounts.filter((/** @type {any} */ account) =>
            account.product.productType !== productTypes.InstantAccessCashISA);
    /** @type {any[]} */
    const pendingAccounts = customer.pendingAccounts || [];
    /** @type {any[]} */
    const products = customer.products || [];
    const visiblePendingAccounts = isFisaMaturityEnabled
        ? pendingAccounts
        : pendingAccounts.filter((/** @type {any} */ pendingAccount) => {
            const product = products.find((/** @type {any} */ item) => item.id === pendingAccount.productId);
            return !product || product.productType !== productTypes.InstantAccessCashISA;
        });

    // Check if any fundable account exists
    const fundableAccountExists = accounts.some((/** @type {any} */ account) => 
        account.accountStatus !== 'Closed' && 
        account.accountStatus !== 'ClosureRequested'
    );

    // Check if we should show ISA allowance widget
    const showIsaAllowance = hasIsaAccount(visibleAccounts);

    // Maturity banner logic
    const frsNtb = products.find((/** @type {any} */ product) => product.code === productKeys.FixedRateSaver);
    const frsNtbRate = frsNtb ? frsNtb.rate : undefined;
    const maturingAccounts = accounts.filter((/** @type {any} */ a) => 
        a.maturity && 
        a.maturity.maturityInstructionWindowStatus === 'InstructionOpen' && 
        !a.maturity.maturityInstruction
    );
    const bannerMaturingAccounts = filterMaturingAccountsForBanner(maturingAccounts, isFisaMaturityEnabled);
    const rollingOverAccounts = accounts.filter((/** @type {any} */ a) => 
        a.maturity && 
        a.maturity.maturityInstruction && 
        a.maturity.maturityInstruction.includes('Rollover') && 
        a.accountStatus !== 'Closed'
    );

    // Rate changes for AER footnotes
    const rateChangesList = accounts
        .filter((/** @type {any} */ account) => 
            account.accountStatus !== 'ClosureRequested' && 
            account.accountStatus !== 'Closed'
        )
        .map((/** @type {any} */ account) => account.product.rateChanges || [])
        .flat();

    // Conditions for AER/Gross footnotes display
    const showMaturityFootnote = (maturingAccounts.length > 0 || rollingOverAccounts.length > 0) && rateChangesList.length === 0;
    const showRateChangeFootnote = maturingAccounts.length === 0 && rollingOverAccounts.length === 0 && rateChangesList.length > 0;
    const showBothFootnotes = (maturingAccounts.length > 0 || rollingOverAccounts.length > 0) && rateChangesList.length > 0;

    return (
        <div className="new-dashboard">
            {/* Additional Account Created Modal */}
            {showAdditionalAccountPopup && (
                <AdditionalAccountCreatedModal closePopup={closeAdditionalAccountPopup} />
            )}

            {/* FATCA Popup */}
            {featureEnabled && featureEnabled('EnableFatcaPopup') && customer.showFatcaPopup && showFatcaPopup && (
                <FatcaPopup closePopup={() => setShowFatcaPopup(false)} />
            )}
            {/* ISA Transfer Marketing Banner */}
            {featureEnabled && <IsaTransferBanner featureEnabled={featureEnabled} />}

            {/* Welcome Section */}
            <WelcomeHeader
                firstName={customer.firstName}
                featureEnabled={featureEnabled}
                showIsaAllowance={showIsaAllowance}
                isaAllowance={customer.isaAllowance}
                showIsaTooltip={showIsaTooltip}
                onToggleIsaTooltip={toggleIsaTooltip}
            />

            {/* Maturity Banner - new info section message design (Figma node 19181:50778) */}
            {bannerMaturingAccounts.length > 0 && (
                <NewDashboardMaturityBanner maturingAccounts={bannerMaturingAccounts} />
            )}

            {/* Application Status: Under Review / Awaiting Decision */}
            {(customer.applicationStatus === 'under_review' || customer.applicationStatus === 'awaiting_decision') && (
                <NoAccountNotification 
                    message="We're reviewing your application and will be in touch shortly by email." 
                />
            )}

            {/* Application Status: Approved but no account yet - show polling loader */}
            {customer.applicationStatus === 'approved_no_account' && (
                <AccountCreating 
                    onAccountCreated={onAccountCreated} 
                    customerId={currentUser.customerId} 
                />
            )}

            {/* Main Content: Only show when approved */}
            {customer.applicationStatus === 'approved' && (
                <>
                    {/* Accounts Section */}
                    <div className="new-dashboard__content">
                        {/* Action buttons - shown at top on mobile/tablet */}
                        <div className="new-dashboard__action-buttons-container">
                            <div className="new-dashboard__action-buttons">
                                {fundableAccountExists && (
                                    <Button 
                                        size='med' 
                                        label="How to make a deposit" 
                                        variant="tertiary" 
                                        onClick={howToFundClick} 
                                    />
                                )}
                                {featureEnabled && featureEnabled('EnableSecondaryAccounts') && (
                                    <Button 
                                        size='med' 
                                        label="Open another account" 
                                        variant="tertiary" 
                                        onClick={openNewAccountClick} 
                                    />
                                )}
                            </div>
                        </div>
                        
                        {/* Section title with buttons inline on desktop */}
                        <div className="new-dashboard__section-header">
                            <h3 className="new-dashboard__section-title">My accounts</h3>
                            <div className="new-dashboard__action-buttons">
                                {fundableAccountExists && (
                                    <Button 
                                        size='med' 
                                        label="How to make a deposit" 
                                        variant="tertiary" 
                                        onClick={howToFundClick} 
                                    />
                                )}
                                {featureEnabled && featureEnabled('EnableSecondaryAccounts') && (
                                    <Button 
                                        size='med' 
                                        label="Open another account" 
                                        variant="tertiary" 
                                        onClick={openNewAccountClick} 
                                    />
                                )}
                            </div>
                        </div>

                        {/* Account Cards List */}
                        <div className={`new-dashboard__accounts-grid ${carouselEnabled ? 'new-dashboard__accounts-grid--carousel' : ''}`}>
                            <AccountsList
                                accounts={visibleAccounts}
                                customer={customer}
                                pendingAccounts={visiblePendingAccounts}
                                products={products}
                                carouselEnabled={carouselEnabled}
                                selectedAccountActivity={selectedAccountActivity}
                                flippedAccountId={flippedAccountId}
                                actionType={actionType}
                                toAccount={toAccount}
                                onToggleActivity={toggleAccountActivity}
                                onStartWithdrawal={startWithdrawal}
                                onStartDeposit={startDeposit}
                                onStartTransfer={startIsaTransfer}
                                onUnflipCard={unflipCard}
                                onInfoIconClick={handleInfoIconClick}
                                onMaturityInfoIconClick={handleMaturityInfoIconClick}
                                onAvailableBalanceIconClick={handleAvailableBalanceIconClick}
                                onFundingInfoIconClick={handleFundingInfoIconClick}
                                onRateChangeInfoIconClick={handleRateChangeInfoIconClick}
                                openTooltipId={openTooltipId}
                                openMaturityTooltipId={openMaturityTooltipId}
                                openFundingTooltipId={openFundingTooltipId}
                                openRateChangeTooltipId={openRateChangeTooltipId}
                                setActionType={setActionType}
                            />
                        </div>
                    </div>
                </>
            )}

            {/* AER Footnotes — Figma: typography/body/med 14px Inter Medium, node 19182:50782 */}
            {(showMaturityFootnote || showRateChangeFootnote || showBothFootnotes) && (
                <div className="new-dashboard__footnotes">
                    <div className="new-dashboard__footnotes-inner">
                        <p className="new-dashboard__footnote">
                            * AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest was paid and compounded once a year. The AER is intended to be an indicative rate to help you compare the return on different savings products.
                        </p>
                        {(showRateChangeFootnote || showBothFootnotes) && (
                            <p className="new-dashboard__footnote">
                                ^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Feedback Form */}
            <Feedback
                submitFeedback={(/** @type {string} */ rating, /** @type {string} */ comments) => {
                    submitFeedback(rating, comments, /** @type {any} */ (undefined));
                }}
                themeName="monochrome"
                enabled={featureEnabled ? featureEnabled('EnableDashboardFeedbackForm') : false}
            />
            
            {/* How to Fund Modal */}
            {modalOpen && (
                <HowToFundPopup 
                    data={{ customer }} 
                    onClose={() => setModalOpen(false)} 
                />
            )}
            
            {/* Global Tooltips (fixed position, desktop only) */}
            <GlobalTooltips
                accounts={accounts}
                openTooltipId={openTooltipId}
                popupPosition={popupPosition}
                openMaturityTooltipId={openMaturityTooltipId}
                maturityPopupPosition={maturityPopupPosition}
                openFundingTooltipId={openFundingTooltipId}
                fundingPopupPosition={fundingPopupPosition}
                openRateChangeTooltipId={openRateChangeTooltipId}
                rateChangePopupPosition={rateChangePopupPosition}
                featureEnabled={featureEnabled}
            />
        </div>
    );
}

export default withFeatureFlags(NewDashboard);
